// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

const getStatusIcon = (status) => {
  switch (status) {
    case 'Error':
      return <ErrorRoundedIcon color="error" />;
    case 'Warning':
      return <WarningRoundedIcon color="warning" />;
    case 'Success':
      return <CheckCircleRoundedIcon color="success" />;
    case 'Critical':
      return <PriorityHighRoundedIcon color="error" />;
    case 'Unknown':
      return <QuestionMarkRoundedIcon color="gray" />;
    default:
      return null;
  }
};

const formatDaysSince = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();

  if (isNaN(date.getTime())) {
    return '-';
  }

  // Calculate the difference in time (milliseconds)
  const diffTime = Math.abs(now - date);

  // Convert time difference to days
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return `${diffDays} day(s) ago`;
};

/**
 * Gets the MaxLatencyHours from the BillingMeterHealthChecks state of a building.
 * @param {Object} building - The building object containing state information.
 * @returns {number | null} - Returns the MaxLatencyHours value or null if not available.
 */
export function getMaxLatencyHours(building) {
  if (
    building &&
    building.State &&
    building.State.BillingMeterHealthCheck &&
    building.State.BillingMeterHealthCheck.BillingMeterHealthChecks
  ) {
    // Extract MaxLatencyHours from each BillingMeterHealthCheck
    const latencyHours =
      building.State.BillingMeterHealthCheck.BillingMeterHealthChecks.map(
        (check) => check.Data?.MaxLatencyHours,
      ).filter((latency) => latency !== undefined && latency !== null);

    if (latencyHours.length > 0) {
      return Math.max(...latencyHours); // Return the highest MaxLatencyHours found
    }
  }
  return null;
}

export function getMaxLatencyConsumptionHours(building) {
  if (
    building &&
    building.State &&
    building.State.BillingMeterHealthCheck &&
    building.State.BillingMeterHealthCheck.BillingMeterHealthChecks
  ) {
    // Extract MaxLatencyHours from each BillingMeterHealthCheck
    const latencyHours =
      building.State.BillingMeterHealthCheck.BillingMeterHealthChecks.map(
        (check) => check.Data?.ConsumptionLatencyHours,
      ).filter((latency) => latency !== undefined && latency !== null);

    if (latencyHours.length > 0) {
      return Math.max(...latencyHours); // Return the highest MaxLatencyHours found
    }
  }
  return null;
}

export function getJobSubscriptionDataFiltered(building, jobType) {
  const subscriptions = building.JobSubscriptions.filter(
    (subscription) => subscription.JobType === jobType,
  );

  return subscriptions || [];
}

export function getJobSubscriptionData(building, jobType) {
  return building.JobSubscriptions.find(
    (subscription) => subscription.JobType === jobType,
  );
}

export function getQualityOfService(jobSubscription) {
  if (
    jobSubscription &&
    jobSubscription.State &&
    jobSubscription.State.QualityOfService
  ) {
    return jobSubscription.State.QualityOfService.QoS;
  }
  return null; // or return a default value
}

export function getSystemPerformanceStatus(jobSubscription) {
  if (
    jobSubscription &&
    jobSubscription.State &&
    jobSubscription.State.SystemPerformance
  ) {
    return jobSubscription.State.SystemPerformance.Status;
  }
  return 'Unknown'; // Default value if not found
}

export function getBillingMeterHealthStatus(state) {
  if (state && state.BillingMeterHealthCheck) {
    const status = state.BillingMeterHealthCheck.OverallStatus || 'N/A';
    let warnings = [];
    let errors = [];
    let critical_errors = [];
    let critical_warnings = [];

    if (state.BillingMeterHealthCheck.BillingMeterHealthChecks) {
      state.BillingMeterHealthCheck.BillingMeterHealthChecks.forEach(
        (healthCheck) => {
          healthCheck.DataInputHealthChecks.forEach((inputCheck) => {
            if (inputCheck.Errors) {
              errors = errors.concat(inputCheck.Errors);
            }
            if (inputCheck.Warnings) {
              warnings = warnings.concat(inputCheck.Warnings);
            }
            if (inputCheck.Status === 'Critical' && inputCheck.Errors) {
              critical_errors = critical_errors.concat(inputCheck.Errors);
            }
            if (inputCheck.Status === 'Critical' && inputCheck.Warnings) {
              critical_warnings = critical_warnings.concat(inputCheck.Warnings);
            }
          });
        },
      );
    }

    return { status, warnings, errors, critical_errors, critical_warnings };
  }

  return {
    status: 'N/A',
    warnings: [],
    errors: [],
  };
}

export function getBuildingById(account, buildingId) {
  return account.Buildings.find(
    (building) => building.BuildingId === buildingId,
  );
}

export function getEnergySavings(building) {
  if (
    building &&
    building.State &&
    building.State.EnergySavings &&
    building.State.EnergySavings.Data
  ) {
    return (
      building.State.EnergySavings.Data.EnergySavingsSummary
        ?.TotalEnergySavingsPercentage || null
    );
  }
  return null;
}

export function getPreviousEnergySavings(building) {
  const result = [];

  // Loop through each key in the EnergySavingsPreviousYears object
  for (const yearKey in building.State.EnergySavingsPreviousYears) {
    const yearData = building.State.EnergySavingsPreviousYears[yearKey];

    // Ensure the necessary data structure is present
    if (yearData && yearData.Data && yearData.Data.EnergySavingsSummary) {
      const { TotalEnergySavingsPercentage } =
        yearData.Data.EnergySavingsSummary;
      const { StartTime, EndTime } = yearData.Data;

      result.push({
        label: yearKey,
        value: TotalEnergySavingsPercentage,
        startTime: StartTime,
        endTime: EndTime,
      });
    }
  }

  return result;
}

export function getBuildingSortedByEnergySavings(buildings) {
  return buildings
    .map((building) => {
      const energySavings =
        building?.State?.EnergySavings?.TotalEnergySavingsPercentage;
      return {
        building,
        energySavings:
          energySavings !== undefined && energySavings !== null
            ? energySavings
            : null,
      };
    })
    .sort((a, b) => {
      // First, move buildings with null energy savings to the top
      if (a.energySavings === null && b.energySavings !== null) return -1;
      if (a.energySavings !== null && b.energySavings === null) return 1;

      // Now, sort by energy savings (ascending), handling negative and positive floats
      if (a.energySavings !== null && b.energySavings !== null) {
        return a.energySavings - b.energySavings; // Ascending sort
      }

      return 0; // Keep order if both are null (no energy savings)
    })
    .map((item) => item.building); // Return only the sorted buildings
}

export function getBillingMeterHealth(building) {
  if (building && building.State && building.State.BillingMeterHealthCheck) {
    return building.State.BillingMeterHealthCheck;
  }
  return null;
}

export function getSensorHealthStatus(jobSubscription, dataInputName) {
  if (
    jobSubscription &&
    jobSubscription.State &&
    jobSubscription.State.SensorHealthCheck
  ) {
    const check = jobSubscription.State.SensorHealthCheck.DataInputChecks.find(
      (inputCheck) => inputCheck.DataInputName === dataInputName,
    );
    return check ? check.DataInputStatus : 'Unknown';
  }
  return 'Unknown';
}

export function getAccountSummary(account) {
  return {
    accountId: account.AccountId,
    name: account.Name,
    buildings: account.Buildings.map((building) => ({
      buildingId: building.BuildingId,
      name: building.Name,
      address: building.Address,
      predictiveHeatControl: getJobSubscriptionData(
        building,
        'PredictiveHeatControl',
      ),
    })),
  };
}

export function getJobSubscriptionById(building, jobSubscriptionId) {
  return building.JobSubscriptions.find(
    (subscription) => subscription.JobSubscriptionId === jobSubscriptionId,
  );
}

export function getAllJobSubscriptionsByType(account, jobType) {
  return account.Buildings.flatMap((building) =>
    building.JobSubscriptions.filter(
      (subscription) => subscription.JobType === jobType,
    ),
  );
}

export function getLatestJobRunStatus(jobSubscription) {
  if (jobSubscription && jobSubscription.LatestJobRun) {
    return jobSubscription.LatestJobRun.Status;
  }
  return 'NoData'; // Default if not found
}

export function getMachineLearningStatus(jobSubscription, modelName) {
  if (
    jobSubscription &&
    jobSubscription.State &&
    jobSubscription.State.MachineLearning
  ) {
    const modelInfo =
      jobSubscription.State.MachineLearning.MachineLearningInfo.find(
        (model) => model.Name === modelName,
      );
    return modelInfo ? modelInfo.Status : 'Unknown';
  }
  return 'Unknown';
}

export function getLatestJobRunErrorsWarnings(jobSubscription) {
  if (jobSubscription && jobSubscription.LatestJobRun) {
    return {
      errors: jobSubscription.LatestJobRun.Output.errors || [],
      warnings: jobSubscription.LatestJobRun.Output.warnings || [],
    };
  }
  return { errors: [], warnings: [] };
}

export function getAllDataInputStatuses(jobSubscription) {
  if (
    jobSubscription &&
    jobSubscription.State &&
    jobSubscription.State.SensorHealthCheck
  ) {
    return jobSubscription.State.SensorHealthCheck.DataInputChecks.map(
      (check) => ({
        dataInputName: check.DataInputName,
        status: check.DataInputStatus,
      }),
    );
  }
  return [];
}

export function getBuildingOccupiedCalendar(jobSubscription) {
  if (
    jobSubscription &&
    jobSubscription.UserInput &&
    jobSubscription.UserInput.BuildingOccupiedCalendar
  ) {
    return jobSubscription.UserInput.BuildingOccupiedCalendar;
  }
  return [];
}

export function getTargetTemperature(jobSubscription) {
  if (
    jobSubscription &&
    jobSubscription.State &&
    jobSubscription.State.TargetTemperatureStatus
  ) {
    return {
      temperature:
        jobSubscription.State.TargetTemperatureStatus.TargetTemperature,
      status: jobSubscription.State.TargetTemperatureStatus.Status,
    };
  }
  return { temperature: null, status: 'Unknown' };
}

export function isJobSubscriptionActive(jobSubscription) {
  return jobSubscription && jobSubscription.StateCode === 'Active';
}

export function getGatewayHealthStatus(jobSubscription) {
  if (
    jobSubscription &&
    jobSubscription.State &&
    jobSubscription.State.GatewayHealthCheck
  ) {
    return jobSubscription.State.GatewayHealthCheck.OverallStatus;
  }
  return 'Unknown';
}

export function getSpecificWarningsOrErrors(
  jobSubscription,
  type = 'warnings',
) {
  if (
    jobSubscription &&
    jobSubscription.LatestJobRun &&
    jobSubscription.LatestJobRun.Output
  ) {
    return jobSubscription.LatestJobRun.Output[type] || [];
  }
  return [];
}

export function getAggregateTypeForSensor(jobSubscription, sensorType) {
  if (
    jobSubscription &&
    jobSubscription.DataInput &&
    jobSubscription.DataInput[sensorType]
  ) {
    return jobSubscription.DataInput[sensorType].map(
      (input) => input.AggregateType,
    );
  }
  return [];
}

export function getSensorBatteryStateErrors(jobSubscription) {
  if (
    jobSubscription &&
    jobSubscription.State &&
    jobSubscription.State.SensorHealthCheck
  ) {
    return jobSubscription.State.SensorHealthCheck.DataInputChecks.flatMap(
      (check) =>
        check.DataInputHealthChecks?.filter(
          (healthCheck) =>
            healthCheck.Name === 'BatteryStateCheck' &&
            healthCheck.Status === 'Error',
        ).map((healthCheck) => ({
          dataInputName: check.DataInputName,
          message: healthCheck.Errors[0]?.message,
        })) || [],
    );
  }
  return [];
}

export function getLocationInfo(jobSubscription) {
  if (
    jobSubscription &&
    jobSubscription.UserInput &&
    jobSubscription.UserInput.Location
  ) {
    return jobSubscription.UserInput.Location;
  }
  return { Address: '', Latitude: null, Longitude: null };
}

export const getInitializationCheck = (state) => {
  const initialization = state?.Initialization;

  if (!initialization) return null;

  const overallStatusIcon = getStatusIcon(initialization.OverallStatus);

  const installationCheck = initialization.InstallationCheck
    ? {
        name: 'Installation Check',
        icon: getStatusIcon(initialization.InstallationCheck.Status),
        messages: [
          ...(initialization.InstallationCheck.Warnings || []),
          ...(initialization.InstallationCheck.Errors || []),
        ].map((item) => item.message),
      }
    : null;

  const dataInputChecks = initialization.DataInputChecks?.filter(
    (check) => check.DataInputStatus !== 'Success',
  ).map((check) => ({
    name: check.DataInputName,
    icon: getStatusIcon(check.DataInputStatus),
    subChecks: check.DataRawCheck?.filter(
      (subCheck) => subCheck.Status !== 'Success',
    ).map((subCheck) => ({
      deviceIds: subCheck.DeviceIds,
      icon: getStatusIcon(subCheck.Status),
      messages: [...(subCheck.Warnings || []), ...(subCheck.Errors || [])].map(
        (item) => item.message,
      ),
    })),
  }));

  return {
    overallStatusIcon,
    checks: [installationCheck, ...dataInputChecks].filter(Boolean),
  };
};

export const getComfortHealthCheck = (state) => {
  const comfortHealthCheck = state?.ComfortHealthCheck;

  if (!comfortHealthCheck) return null;

  const overallStatusIcon = getStatusIcon(comfortHealthCheck.Status);

  const comfortChecks = comfortHealthCheck.ComfortHealthChecks?.filter(
    (check) => check.Status !== 'Success',
  ).map((check) => ({
    name: check.Name,
    icon: getStatusIcon(check.Status),
    messages: [...(check.Warnings || []), ...(check.Errors || [])].map(
      (item) => item.message,
    ),
    deviceIds: check.DeviceIds,
  }));

  return {
    overallStatusIcon,
    checks: comfortChecks,
    updateTime: formatDaysSince(comfortHealthCheck.UpdateTime),
    targetTemperatureAccuracyLatest:
      comfortHealthCheck.Data?.TargetTemperatureAccuracyLatest,
    targetTemperatureAccuracyMean:
      comfortHealthCheck.Data?.TargetTemperatureAccuracyMean,
  };
};

// Utility function to get MachineLearning information
export const getMachineLearningCheck = (state) => {
  const machineLearning = state?.MachineLearning;
  if (!machineLearning) return null;

  const overallStatusIcon = getStatusIcon(machineLearning.OverallStatus);

  const checks = machineLearning.MachineLearningInfo?.map((check) => ({
    name: check.Name,
    icon: getStatusIcon(check.Status),
    messages: [...(check.Warnings || []), ...(check.Errors || [])].map(
      (item) => item.message,
    ),
    status: check.Status,
    updateTime: formatDaysSince(check.UpdateTime),
    modelRefreshTime: formatDaysSince(check.ModelRefreshTime),
  }));

  return {
    overallStatusIcon,
    checks,
  };
};

// Utility function to get TargetTemperatureStatus information
export const getTargetTemperatureCheck = (state) => {
  const targetTemperatureStatus = state?.TargetTemperatureStatus;

  if (!targetTemperatureStatus) return null;

  const overallStatusIcon = getStatusIcon(targetTemperatureStatus.Status);

  const messages = [
    ...(targetTemperatureStatus.Warnings || []),
    ...(targetTemperatureStatus.Errors || []),
  ].map((item) => item.message);

  return {
    overallStatusIcon,
    checks: [
      {
        name: 'Target Temperature Status',
        icon: overallStatusIcon,
        messages,
      },
    ],
  };
};

// Utility function to get SystemPerformance information
export const getSystemPerformanceCheck = (state) => {
  const systemPerformance = state?.SystemPerformance;

  if (!systemPerformance) return null;

  const overallStatusIcon = getStatusIcon(systemPerformance.Status);

  const checks = systemPerformance.SystemHealthChecks?.filter(
    (check) => check.Status !== 'Success',
  ).map((check) => ({
    name: check.Name,
    icon: getStatusIcon(check.Status),
    messages: [...(check.Warnings || []), ...(check.Errors || [])].map(
      (item) => item.message,
    ),
  }));

  return {
    overallStatusIcon,
    checks,
  };
};

// Utility function to get QualityOfService information
export const getQualityOfServiceCheck = (state) => {
  const qualityOfService = state?.QualityOfService;

  if (!qualityOfService) return null;

  const overallStatusIcon = getStatusIcon(qualityOfService.Status);

  const messages = [
    ...(qualityOfService.Warnings || []),
    ...(qualityOfService.Errors || []),
  ].map((item) => item.message);

  return {
    overallStatusIcon,
    checks: [
      {
        name: 'QualityOfService',
        icon: overallStatusIcon,
        messages,
      },
    ],
  };
};

// Utility function to get FrostProtection information
export const getFrostProtectionCheck = (state) => {
  const frostProtection = state?.FrostProtection;

  if (!frostProtection) return null;

  const overallStatusIcon = getStatusIcon(
    frostProtection.FrostProtectionStatus,
  );

  return {
    overallStatusIcon,
    checks: [],
  };
};

// Function to get SensorHealthCheck information
export const getSensorHealthCheck = (state) => {
  const sensorHealthCheck = state?.SensorHealthCheck;

  if (!sensorHealthCheck) return null;

  const overallStatusIcon = getStatusIcon(sensorHealthCheck.OverallStatus);

  // Filter out DataInputChecks that have 'Success' status
  const filteredChecks = sensorHealthCheck.DataInputChecks?.filter(
    (check) => check.DataInputStatus !== 'Success',
  ).map((check) => {
    const icon = getStatusIcon(check.DataInputStatus);
    const subChecks = check.DataInputHealthChecks?.filter(
      (subCheck) => subCheck.Status !== 'Success',
    ).map((subCheck) => ({
      name: subCheck.Name,
      icon: getStatusIcon(subCheck.Status),
      messages: [...(subCheck.Warnings || []), ...(subCheck.Errors || [])].map(
        (item) => item.message,
      ),
    }));

    return {
      name: check.DataInputName,
      icon,
      subChecks,
    };
  });

  return {
    overallStatusIcon,
    checks: filteredChecks,
  };
};

// Function to get GatewayHealthCheck information
export const getGatewayHealthCheck = (state) => {
  const gatewayHealthCheck = state?.GatewayHealthCheck;

  if (!gatewayHealthCheck) return null;

  const overallStatusIcon = getStatusIcon(gatewayHealthCheck.OverallStatus);

  // Handle specific checks within GatewayHealthCheck
  const checks = ['UpTimeCheck', 'ActuatorPlanCheck']
    .map((checkKey) => {
      const check = gatewayHealthCheck[checkKey];
      if (check && check.Status !== 'Success') {
        return {
          name: check.Name,
          icon: getStatusIcon(check.Status),
          messages: [...(check.Warnings || []), ...(check.Errors || [])].map(
            (item) => item.message,
          ),
        };
      }
      return null;
    })
    ?.filter(Boolean);

  return {
    overallStatusIcon,
    checks,
  };
};
